import { createAsyncThunk } from "@reduxjs/toolkit";
import apiAxios from "../../../services/apiAxios";
import {
  setQuickOrders,
  setStoreQuickOrders,
  setLastQuickOrderId,
  setQuickOrderLoading,
  setQuickOrderError,
} from "../../Reducer/QuickOrder/QuickOrderSlice";
import toast from "react-hot-toast";

/**
 * Upload a prescription file for Quick Order
 * Returns the S3 URL of the uploaded file
 */
export const uploadQuickOrderFile = createAsyncThunk(
  "quickOrder/uploadFile",
  async (file, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiAxios.post("/api/quick-order/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.fileUrl) {
        return response.data.fileUrl;
      }
      throw new Error("Upload failed — no URL returned");
    } catch (error) {
      const message = error?.response?.data?.error || error.message || "File upload failed";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

/**
 * Create a Quick Order
 */
export const createQuickOrder = createAsyncThunk(
  "quickOrder/create",
  async ({ patient, qoUploadUrl, qoNotes, paymentMethod, deliveryMethod }, { dispatch, rejectWithValue }) => {
    dispatch(setQuickOrderLoading(true));
    try {
      const response = await apiAxios.post("/api/quick-order", {
        patient,
        qoUploadUrl,
        qoNotes,
        paymentMethod,
        deliveryMethod,
      });

      if (response?.status === 201 && response?.data?.QID) {
        dispatch(setLastQuickOrderId(response.data.QID));
        toast.success(`Quick Order ${response.data.QID} placed successfully!`);
        dispatch(setQuickOrderLoading(false));
        return response.data;
      }
      throw new Error("Failed to create Quick Order");
    } catch (error) {
      const message = error?.response?.data?.error || error.message || "Failed to create Quick Order";
      toast.error(message);
      dispatch(setQuickOrderError(message));
      dispatch(setQuickOrderLoading(false));
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch all quick orders for the authenticated doctor (Tracking page)
 */
export const fetchDoctorQuickOrders = createAsyncThunk(
  "quickOrder/fetchDoctor",
  async (_, { dispatch }) => {
    try {
      const response = await apiAxios.get("/api/quick-order/doctor");
      if (response?.data) {
        dispatch(setQuickOrders(response.data));
      }
      return response?.data;
    } catch (error) {
      console.warn("fetchDoctorQuickOrders error:", error.message);
      dispatch(setQuickOrderError(error.message));
    }
  }
);

/**
 * Fetch all pending quick orders for the authenticated store (Dashboard)
 */
export const fetchStoreQuickOrders = createAsyncThunk(
  "quickOrder/fetchStore",
  async (_, { dispatch }) => {
    try {
      const response = await apiAxios.get("/api/quick-order/store");
      if (response?.data) {
        dispatch(setStoreQuickOrders(response.data));
      }
      return response?.data;
    } catch (error) {
      console.warn("fetchStoreQuickOrders error:", error.message);
      dispatch(setQuickOrderError(error.message));
    }
  }
);

/**
 * Fetch a single quick order by QID
 */
export const fetchQuickOrderByQID = createAsyncThunk(
  "quickOrder/fetchByQID",
  async (qid, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`/api/quick-order/${qid}`);
      return response?.data;
    } catch (error) {
      const message = error?.response?.data?.error || error.message;
      return rejectWithValue(message);
    }
  }
);

/**
 * Cancel a quick order with reason
 */
export const cancelQuickOrder = createAsyncThunk(
  "quickOrder/cancel",
  async ({ qid, cancelReason }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiAxios.patch(`/api/quick-order/cancel/${qid}`, {
        cancelReason,
      });

      if (response?.status === 200) {
        toast.success("Quick Order cancelled successfully");
        // Re-fetch lists to update UI
        dispatch(fetchDoctorQuickOrders());
        dispatch(fetchStoreQuickOrders());
        return { QID: qid, status: "cancelled" };
      }
      throw new Error("Failed to cancel Quick Order");
    } catch (error) {
      const message = error?.response?.data?.error || error.message || "Failed to cancel Quick Order";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);
